/**
 * CodexHighlight — Tiptap extension for Story Bible inline highlighting.
 *
 * Uses a ProseMirror plugin with DecorationSet to non-destructively mark
 * every occurrence of a bible entry name in the manuscript. Decorations are
 * rebuilt whenever entries change (via the `setCodexEntries` command) or
 * whenever the document itself changes.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, EditorStateConfig, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';
import type { BibleEntryType } from './api';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Minimal shape needed by the plugin — avoids importing full BibleEntry */
export interface CodexEntry {
  id: string;
  name: string;
  type: BibleEntryType;
}

// ---------------------------------------------------------------------------
// Plugin state
// ---------------------------------------------------------------------------

interface CodexPluginState {
  entries: CodexEntry[];
  decorations: DecorationSet;
}

const CODEX_KEY = new PluginKey<CodexPluginState>('codexHighlight');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Escape all regex-special characters so entry names are matched literally */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Scan every text node in `doc` and produce inline decorations for every
 * occurrence of an entry name (case-insensitive, whole-word boundary).
 * Longest names are tried first to avoid partial overlaps.
 */
function buildDecorations(doc: PmNode, entries: CodexEntry[]): DecorationSet {
  // Remove blank names before sorting
  const sorted = [...entries]
    .filter((e) => e.name.trim().length > 0)
    .sort((a, b) => b.name.length - a.name.length);

  if (sorted.length === 0) return DecorationSet.empty;

  // O(1) lookup by lowercase name
  const byName = new Map<string, CodexEntry>();
  for (const e of sorted) {
    byName.set(e.name.toLowerCase(), e);
  }

  // Build one combined alternation using Unicode-aware word boundaries so that
  // accented/non-ASCII names (e.g. "Antônio", "Inês") are matched correctly.
  // JS \b is ASCII-only and fails around accented chars, so we use lookarounds
  // with the Unicode category \p{L} (letter) and \p{N} (number) instead.
  // The `u` flag enables \p{…} support; keep `g` and `i` for global case-insensitive search.
  const alts = sorted.map((e) => `(?:${escapeRegex(e.name)})`).join('|');

  let regex: RegExp;
  try {
    // Unicode-aware boundaries: match only when NOT preceded/followed by a word char
    regex = new RegExp(`(?<![\\p{L}\\p{N}_])(?:${alts})(?![\\p{L}\\p{N}_])`, 'giu');
  } catch {
    // Fallback (very large bible or exotic name chars): skip decoration entirely
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    regex.lastIndex = 0;
    let m: RegExpExecArray | null;

    while ((m = regex.exec(node.text)) !== null) {
      const entry = byName.get(m[0].toLowerCase());
      if (!entry) continue;

      const from = pos + m.index;
      const to = from + m[0].length;

      decorations.push(
        Decoration.inline(from, to, {
          class: `codex-entity codex-${entry.type}`,
          'data-codex-id': entry.id,
          'data-codex-name': entry.name,
          'data-codex-type': entry.type,
        }),
      );
    }
  });

  return DecorationSet.create(doc, decorations);
}

// ---------------------------------------------------------------------------
// Command type augmentation
// ---------------------------------------------------------------------------

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    codexHighlight: {
      /** Replace the codex entry list and recompute all inline highlights */
      setCodexEntries: (entries: CodexEntry[]) => ReturnType;
    };
  }
}

// ---------------------------------------------------------------------------
// Extension
// ---------------------------------------------------------------------------

export const CodexHighlight = Extension.create({
  name: 'codexHighlight',

  addCommands() {
    return {
      setCodexEntries:
        (entries: CodexEntry[]) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            dispatch(tr.setMeta(CODEX_KEY, { entries }));
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<CodexPluginState>({
        key: CODEX_KEY,

        state: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          init(_config: EditorStateConfig, _editorState: EditorState): CodexPluginState {
            return { entries: [], decorations: DecorationSet.empty };
          },

          apply(
            tr: Transaction,
            old: CodexPluginState,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _prevState: EditorState,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _nextState: EditorState,
          ): CodexPluginState {
            // Explicit entry update via command meta
            const meta = tr.getMeta(CODEX_KEY) as { entries: CodexEntry[] } | undefined;
            if (meta?.entries !== undefined) {
              return {
                entries: meta.entries,
                decorations: buildDecorations(tr.doc, meta.entries),
              };
            }

            // No doc change → no work needed
            if (!tr.docChanged) return old;

            // Doc changed but no entries → keep empty state cheaply
            if (old.entries.length === 0) return old;

            // Doc changed → MAP existing decorations to their new positions
            // (cheap). A full rescan only happens when the entries change
            // (setCodexEntries) — so typing stays smooth even with many entries.
            return {
              entries: old.entries,
              decorations: old.decorations.map(tr.mapping, tr.doc),
            };
          },
        },

        props: {
          // Return the current decoration set for ProseMirror to render
          decorations(state: EditorState): DecorationSet | null | undefined {
            return CODEX_KEY.getState(state)?.decorations;
          },
        },
      }),
    ];
  },
});
